import { fetch } from 'http-common';

import { PostData } from 'types';
import { ListType } from 'types/api/api.type';
import { WarehouseData } from 'types/api/customer.type';

type WarehouseDataList = Omit<ListType, 'content'> & { content: WarehouseData[] };

/**
 * `{id, customerId, customerWarehouseId, status, timestamp, userId,
 * customerWarehouse: {...fields}}` — same envelope shape as Customer/Shop,
 * flattened the same way right after the HTTP call.
 */
type WarehouseRecordEnvelope = {
  customerWarehouseId: string;
  status: string;
  customerWarehouse: Record<string, unknown>;
};

const flattenWarehouse = (envelope: WarehouseRecordEnvelope): WarehouseData =>
  ({
    ...envelope.customerWarehouse,
    id: envelope.customerWarehouseId,
    status: envelope.status,
  } as WarehouseData);

class WarehousesDataService {
  private http = fetch();

  // eslint-disable-next-line
  private ep = (id: string) => `/v1/customer-mapper/customers/${id}/warehouses`;

  public async getAll(id: string) {
    const response = await this.http.get<Omit<ListType, 'content'> & { content: unknown[] }>(
      this.ep(id)
    );

    return {
      ...response,
      data: {
        ...response.data,
        content: (response.data.content as WarehouseRecordEnvelope[]).map(flattenWarehouse),
      },
    } as { data: WarehouseDataList };
  }

  public async get(id: string, warehouseId: string) {
    const response = await this.http.get<WarehouseRecordEnvelope>(
      `${this.ep(id)}/${warehouseId}`
    );

    return { ...response, data: flattenWarehouse(response.data) };
  }

  /** Soft delete (any status except IN_REVIEW). For a non-admin deleting an
   * ACCEPTED warehouse, the backend appends DELETE_REQUESTED instead of
   * actually deleting — the caller (TableActions.tsx) checks the returned
   * status to tell which happened (IOOAEC-444). Was returning the raw,
   * unflattened envelope (no top-level `id`, unlike every other method
   * here) — same bug as shops.service.ts's deleteShop, fixed the same way. */
  public async deleteWarehouse(id: string, warehouseId: string) {
    const response = await this.http.delete<WarehouseRecordEnvelope>(
      `${this.ep(id)}/${warehouseId}`
    );
    return { ...response, data: flattenWarehouse(response.data) };
  }

  public async post(id: string, data: PostData) {
    const response = await this.http.post<WarehouseRecordEnvelope>(this.ep(id), data);

    return { ...response, data: flattenWarehouse(response.data) };
  }

  public async put(id: string, warehouseId: string, data: PostData) {
    const response = await this.http.put<WarehouseRecordEnvelope>(
      `${this.ep(id)}/${warehouseId}`,
      data
    );

    return { ...response, data: flattenWarehouse(response.data) };
  }

  public async postSubmit(id: string, warehouseId: string) {
    const response = await this.http.post<WarehouseRecordEnvelope>(
      `${this.ep(id)}/${warehouseId}/submit`
    );

    return { ...response, data: flattenWarehouse(response.data) };
  }
}

const warehousesDataService = new WarehousesDataService();

export { warehousesDataService };
